import { useEffect, useRef, useState } from 'react';
import * as Y from 'yjs';

interface ComputedValues {
  [cellId: string]: any;
}

export function useComputeWorker(cellsMap: Y.Map<string>, mapName: string) {
  const workerRef = useRef<Worker | null>(null);
  const [computedValues, setComputedValues] = useState<ComputedValues>({});

  useEffect(() => {
    if (!cellsMap || !cellsMap.doc) return;
    const ydoc = cellsMap.doc;

    workerRef.current = new Worker(
      new URL('../engine/compute.worker.ts', import.meta.url),
      { type: 'module' }
    );

    workerRef.current.onmessage = (event) => {
      const { type, updates } = event.data;
      if (type === 'BATCH_UPDATE') {
        setComputedValues((prev) => ({
          ...prev,
          ...updates,
        }));
      }
    };

    // Safely observe only changes directly made to the targeted cellsMap
    const handleMapObserve = (event: Y.YMapEvent<string>) => {
      const changedKeys = Array.from(event.changes.keys.keys());
      const update = Y.encodeStateAsUpdate(ydoc);
      
      changedKeys.forEach(key => {
          workerRef.current?.postMessage({
              type: 'RECALCULATE',
              ydocUpdate: update,
              changedCellId: key,
              mapName: mapName
          });
      });
    };

    cellsMap.observe(handleMapObserve);

    // Initial compute on mount to hydrate existing data
    const stateUpdate = Y.encodeStateAsUpdate(ydoc);
    Array.from(cellsMap.keys()).forEach(key => {
        workerRef.current?.postMessage({
            type: 'RECALCULATE',
            ydocUpdate: stateUpdate,
            changedCellId: key,
            mapName: mapName
        });
    });

    return () => {
      workerRef.current?.terminate();
      cellsMap.unobserve(handleMapObserve);
    };
  }, [cellsMap, mapName]);

  return { computedValues };
}